import { useEffect, useState } from 'react';
import { api } from '../../api/client';

// ─────────────────────────────────────────────────────────────────────────────────────────────
// "SHOW WHAT SENDS BEFORE IT GOES" (Carla, 2026-09-21) — ONE COMPONENT, EVERY PRE-SEND SURFACE.
//
// Item 7 was sized at 20 hours because about ten surfaces would each have to work out the sender.
// They do not: GET /api/email/which-mailbox answers the question once, and this renders the
// answer. A surface adds one line.
//
// WHAT IT WILL NOT DO IS INVENT A NAME. The backend states carriesName: false, because
// lib/sendViaConnection builds the header as `fromName ? "name <addr>" : addr` and only three of
// about twenty callers pass fromName — so most client emails go out as a bare address. Showing
// "Carla Speight <…>" here would promise a header that usually will not carry a name. A pre-send
// display that lies is worse than none, because it is believed.
//
// AND IT SAYS WHY, not just which. Three of the five answers are something to act on: no default
// among several mailboxes, no mailbox at all, and a chosen mailbox that was ignored because it
// belongs to another workspace. Each names the fix rather than only the state.
//
// IT RENDERS SOMETHING IN EVERY STATE INCLUDING FAILURE. A pre-send line that silently disappears
// when the lookup fails teaches a person that its absence means "nothing to say" — and the one time
// it matters, absence will mean "we could not tell you".
// ─────────────────────────────────────────────────────────────────────────────────────────────

const WRAP = {
  fontSize: '0.78rem',
  color: '#4b5563',
  display: 'flex',
  alignItems: 'baseline',
  gap: '0.35rem',
  flexWrap: 'wrap',
  lineHeight: 1.45,
};

// The three answers a person can act on. Keyed by the backend's stable reason code, so the wording
// lives in one place and a new reason shows up as a missing entry rather than a wrong sentence.
const ACTIONABLE = {
  none_several: 'Choose a default mailbox in Settings → Email so your client sees your own address.',
  none_connected: 'Connect a mailbox in Settings → Email so your client sees your own address.',
  affinity_wrong_workspace: 'The mailbox you chose for this recipient belongs to another workspace, so this one was used instead.',
};

export default function SendingFrom({ to = null, workspace = false, style = {} }) {
  const [state, setState] = useState({ status: 'loading', data: null });

  useEffect(() => {
    let live = true;
    const qs = new URLSearchParams();
    if (to) qs.set('to', to);
    if (workspace) qs.set('workspace', 'true');
    setState({ status: 'loading', data: null });
    api(`/api/email/which-mailbox${qs.toString() ? `?${qs}` : ''}`)
      .then(res => res.json())
      .then(data => { if (live) setState({ status: data && data.error ? 'error' : 'ready', data }); })
      // A failed lookup SAYS so. It must not look like "nothing to tell you".
      .catch(() => { if (live) setState({ status: 'error', data: null }); });
    return () => { live = false; };
  }, [to, workspace]);

  if (state.status === 'loading') {
    return <div data-testid="sending-from" style={{ ...WRAP, ...style }}>Working out which mailbox will send…</div>;
  }

  if (state.status === 'error' || !state.data) {
    return (
      <div data-testid="sending-from" style={{ ...WRAP, ...style, color: '#92400e' }}>
        <span data-testid="sending-from-unknown">
          Aidey could not work out which mailbox will send this. It will still send, check
          Settings → Email if the sender matters here.
        </span>
      </div>
    );
  }

  const { fromAddress, viaPlatform, reason, mailboxCount } = state.data;
  const advice = ACTIONABLE[reason];

  return (
    <div data-testid="sending-from" style={{ ...WRAP, ...style }}>
      <span style={{ color: '#6b7280' }}>From:</span>
      {viaPlatform ? (
        <strong data-testid="sending-from-address" style={{ fontWeight: 600 }}>Aidey, with your reply-to address</strong>
      ) : (
        <strong data-testid="sending-from-address" style={{ fontWeight: 600 }}>{fromAddress}</strong>
      )}
      {advice && (
        <span data-testid="sending-from-advice" style={{ color: '#92400e' }}>
          {advice}
          {reason === 'none_several' && mailboxCount > 1 ? ` You have ${mailboxCount} connected.` : ''}
        </span>
      )}
    </div>
  );
}
